/**
 * "Can't sell" (TMMPDM-0025, tmm-api 0082). TMMPDM-0030 (tmm-api 0095): the cause is the server's
 * `sellableReason` — the ONLY input to the label, the body and the action (composition rule). The
 * console never works out a reason itself and never compares the tenant's currency with a rate
 * card. `null` → no chip, no alert; a value outside the four renders the fallback with its code.
 * TMMPDM-0032 (tmm-api 0097): `NO_RATE_CARD` (action: open the Rate card tab) replaces
 * `NO_CURRENT_OFFER`, which left the contract; a stray one renders the unknown-reason fallback.
 */
import { Alert, AlertTitle, Button, Stack, Tooltip } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Link as RouterLink } from 'react-router-dom';
import { useCopy } from '@/hooks/useCopy';
import type { TenantView } from '@/api/tmm/types';
import { StatusChip } from '@/status';

type SellableSubject = Pick<TenantView, 'sellableReason' | 'reseller' | 'currency'>;

type ReasonAction = 'openAgreement' | 'editTenant' | 'openRateCard';

interface ReasonCopy {
  label: string;
  body: string;
  actions: ReasonAction[];
}

/** The server's reason, rendered; `null` when there is none (the tenant sells). */
function useReasonCopy(tenant: SellableSubject): ReasonCopy | null {
  const { t } = useCopy();
  const reason = tenant.sellableReason;
  switch (reason) {
    case null:
      return null;
    case 'NO_RESELLER':
      return {
        label: t('tenants.sellable.noReseller.label', 'No reseller'),
        body: t(
          'tenants.sellable.noReseller.body',
          "This tenant can't sell until it belongs to a reseller.",
        ),
        actions: [],
      };
    case 'NO_AGREEMENT':
      return {
        label: t('tenants.sellable.noAgreement.label', 'No agreement'),
        body: t('tenants.sellable.noAgreement.body', 'Its reseller has no current agreement.'),
        actions: ['openAgreement'],
      };
    case 'NO_RATE_CARD':
      return {
        label: t('tenants.sellable.noRateCard.label', 'No rate card'),
        body: t(
          'tenants.sellable.noRateCard.body',
          "This tenant can't sell until its reseller activates a rate card.",
        ),
        actions: ['openRateCard'],
      };
    case 'CURRENCY_NOT_PRICED':
      return {
        label: t('tenants.sellable.currencyNotPriced.label', 'Currency not priced'),
        body: t(
          'tenants.sellable.currencyNotPriced.body',
          "The tenant's currency ({{currency}}) isn't the currency of its reseller's rate card.",
          { currency: tenant.currency },
        ),
        actions: ['openAgreement', 'editTenant'],
      };
    default:
      return {
        label: t('tenants.sellable.unknown.label', "Can't sell ({{code}})", {
          code: String(reason),
        }),
        body: t(
          'tenants.sellable.unknown.body',
          "Check this tenant's reseller, agreement, currency and offers.",
        ),
        actions: [],
      };
  }
}

/**
 * Outlined warning chip with its icon, only when there is a reason. The text stays "Can't sell"
 * (column stability); the reason's label is its tooltip.
 */
export function SellableChip({ tenant }: { tenant: SellableSubject }) {
  const { t } = useCopy();
  const reason = useReasonCopy(tenant);
  if (reason === null) return null;
  return (
    <Tooltip title={reason.label} describeChild>
      <StatusChip
        tone="warning"
        icon={<WarningAmberIcon />}
        tabIndex={0}
        label={t('tenants.sellable.chip', "Can't sell")}
      />
    </Tooltip>
  );
}

interface NotSellableAlertProps {
  tenant: SellableSubject;
  /** The reseller's display name, for "Open {{reseller}}'s agreement". */
  resellerName: string;
  /** Opens Edit tenant (CURRENCY_NOT_PRICED). */
  onEditTenant: () => void;
}

/** The Overview's info alert: the reason's label as its title, what it means, and its actions. */
export function NotSellableAlert({ tenant, resellerName, onEditTenant }: NotSellableAlertProps) {
  const { t } = useCopy();
  const reason = useReasonCopy(tenant);
  if (reason === null) return null;

  const renderAction = (action: ReasonAction) => {
    switch (action) {
      case 'openAgreement':
        return !tenant.reseller ? null : (
          <Button
            key={action}
            color="inherit"
            size="small"
            component={RouterLink}
            to={`/partners/${tenant.reseller?.id}?tab=agreement`}
          >
            {t('tenants.notPriced.openAgreement', "Open {{resellerName}}'s agreement", {
              resellerName,
            })}
          </Button>
        );
      case 'editTenant':
        return (
          <Button key={action} color="inherit" size="small" onClick={onEditTenant}>
            {t('tenants.sellable.editTenant', 'Edit tenant…')}
          </Button>
        );
      case 'openRateCard':
        return (
          <Button
            key={action}
            color="inherit"
            size="small"
            component={RouterLink}
            to="?tab=priceCard"
          >
            {t('tenants.sellable.openCustomerPrices', 'Open rate card')}
          </Button>
        );
    }
  };

  return (
    <Alert severity="info" role="status" sx={{ mt: 1 }}>
      <AlertTitle>{reason.label}</AlertTitle>
      {reason.body}
      {reason.actions.length > 0 ? (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          useFlexGap
          sx={{ mt: 1, flexWrap: 'wrap', alignItems: { xs: 'flex-start', sm: 'center' } }}
        >
          {reason.actions.map(renderAction)}
        </Stack>
      ) : null}
    </Alert>
  );
}
